<template>
  <div>
    <template v-for="fg of fieldConfigs">
      <!-- Simple Input -->
      <div v-if="fg.template === 'input'" class="group">
        <label>{{fg.tr}}</label>
        <input :type="fg.type || 'text'" v-model="data[fg.key]">
      </div>

      <!-- Yes|No -->
      <div v-if="fg.template === 'bool'" class="group">
        <label>{{fg.tr}}</label>
        <select v-model="data[fg.key]">
          <option :value="true">{{$t('yes')}}</option>
          <option :value="false">{{$t('no')}}</option>
        </select>
      </div>

      <!-- Translation -->
      <div v-if="fg.template === 'trans'" class="group">
        <LocalizedInput :title="fg.tr" :translate="data[fg.key]" />
      </div>

      <!-- Vue-select -->
      <div v-if="fg.template === 'vue-select'" class="group">
        <label>{{fg.tr}}</label>
        <VueSelect :multiple="fg.multiple || false" v-model="data[fg.key]" taggable>
          <span v-if="fg.no_options" slot="no-options">{{$t(fg.no_options)}}</span>
        </VueSelect>
      </div>

      <!-- Hours -->
      <div v-if="fg.template === 'hours'" class="group week_hours">
        <div class="subtitle">{{$t(fg.tr)}}</div>
        <div class="columns">
          <div class="column">
            <DayTimeRange :day="data[fg.key].monday" dayName="monday"/>
            <DayTimeRange :day="data[fg.key].tuesday" dayName="tuesday"/>
            <DayTimeRange :day="data[fg.key].wednesday" dayName="wednesday"/>
            <DayTimeRange :day="data[fg.key].thursday" dayName="thursday"/>
          </div>
          <div class="column">
            <DayTimeRange :day="data[fg.key].friday" dayName="friday"/>
            <DayTimeRange :day="data[fg.key].saturday" dayName="saturday"/>
            <DayTimeRange :day="data[fg.key].sunday" dayName="sunday"/>
          </div>
        </div>
      </div>

    </template>
    <div>
      <button class="btn btn-primary" @click="save">{{$t('save')}}</button>
    </div>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import VueSelect from 'vue-select';
import LocalizedInput from "@/components/tools/LocalizedInput";
import DayTimeRange from "@/components/tools/DayTimeRange";

export default {
  name: "QuickCompanySetting",

  components: { LocalizedInput, DayTimeRange, VueSelect },

  props: {
    fields: Array,
    branchId: {
      type: Number|String,
      required: true
    },
    companyId: {
      type: Number|String,
      required: true
    },
  },
  
  data() {
    return {
      config: {
        'emergencyCloseManually': {template: 'bool', tr: 'settings.emergency_close'},
        'emergencyCloseManuallyMessage': {template: 'trans', tr: 'settings.emergency_close_message'},
        'bypassPosMode': {template: 'bool', tr: 'settings.bypass_pos_mode'},
        'bypassPosOptions.emails': {template: 'vue-select', tr: 'settings.bypass_pos_mode_emails', multiple: true, no_options: 'settings.email_tip'},

        'onlineOrdering.takeout.general.averageDelay': {template: 'input', type: 'number', tr: ['deliveryMethod.takeout', 'settings.delay'], if: 'onlineOrdering.takeout.activated'},
        'onlineOrdering.delivery.general.averageDelay': {template: 'input', type: 'number', tr: ['deliveryMethod.delivery', 'settings.delay'], if: 'onlineOrdering.delivery.activated'},
        'onlineOrdering.inPlace.general.averageDelay': {template: 'input', type: 'number', tr: ['deliveryMethod.inPlace', 'settings.delay'], if: 'onlineOrdering.inPlace.activated'},
        'onlineOrdering.hotel.general.averageDelay': {template: 'input', type: 'number', tr: ['deliveryMethod.hotel', 'settings.delay'], if: 'onlineOrdering.hotel.activated'},
        'onlineOrdering.catering.general.averageDelay': {template: 'input', type: 'number', tr: ['deliveryMethod.catering', 'settings.delay'], if: 'onlineOrdering.catering.activated'},

        'onlineOrdering.takeout.hours': {template: 'hours', tr: ['open_hours', 'deliveryMethod.takeout'], if: 'onlineOrdering.takeout.activated'},
        'onlineOrdering.delivery.hours': {template: 'hours', tr: ['open_hours', 'deliveryMethod.delivery'], if: 'onlineOrdering.delivery.activated'},
        'onlineOrdering.inPlace.hours': {template: 'hours', tr: ['open_hours', 'deliveryMethod.inPlace'], if: 'onlineOrdering.inPlace.activated'},
        'onlineOrdering.hotel.hours': {template: 'hours', tr: ['open_hours', 'deliveryMethod.hotel'], if: 'onlineOrdering.hotel.activated'},
        'onlineOrdering.catering.hours': {template: 'hours', tr: ['open_hours', 'deliveryMethod.catering'], if: 'onlineOrdering.catering.activated'},
      },
      data: {},
      company: null,
    }
  },

  computed: {
    fieldConfigs() {
      return this.fields
          .map(f => {
            let obj = Object.assign({key: f}, (this.config[f] || {template: 'input'}))
            if (Array.isArray(obj.tr)) {
              obj.tr = obj.tr.map(e => this.$t(e)).join(' - ')
            } else if (typeof obj.tr === 'string') {
              obj.tr = this.$t(obj.tr)
            }
            return obj
          })
          .filter(f => !f.if || this.getKeyInCompany(f.if))
    },
  },

  watch: {
    companyId: {
      immediate: true,
      handler() {
        this.load()
      }
    },
    fields: {
      immediate: true,
      handler() {
        this.resetData()
      }
    },
    company() {
      this.resetData()
    }
  },

  methods: {

    ...mapActions(['getCompany', 'saveCompany']),

    async load() {
      this.$store.commit("showSpinner", true);
      let response = await this.getCompany({
        branchId: this.branchId,
        companyId: this.companyId,
        fallback: true
      });
      this.$store.commit("showSpinner", false);
      this._fixCompany(response)
      this.company = response;
    },

    async save() {
      let morphedData = {}
      for (let [key, value] of Object.entries(this.data)) {
        let fragments = key.split('.')

        /* always put everything from the root key */
        let rootKey = fragments[0];
        if (!morphedData.hasOwnProperty(rootKey)) {
          morphedData[rootKey] = this.getKeyInCompany(rootKey)
        }

        /* modify data */
        let curr = morphedData;
        for (let [i, f] of fragments.entries()) {
          if (i === fragments.length-1) {
            curr[f] = value
          } else {
            curr = curr[f]
          }
        }
      }
      this.$store.commit("showSpinner", true);
      let response = await this.saveCompany({
        branchId: this.branchId,
        companyId: this.companyId,
        fallback: true,
        data: morphedData
      });
      this.$store.commit("showSpinner", false);
      this._fixCompany(response)
      this.company = response;
      if (response) {
        toast(this.$t('quick_company.save_success'), 'success')
      } else {
        toast(this.$t("error.no_server"), "error");
      }
    },

    resetData() {
      this.data = {}
      for(let fg of this.fieldConfigs) {
        let key = fg.key;
        let t = fg.template;
        let value = null;
        let dataInCompany = this.getKeyInCompany(key)
        if (dataInCompany !== undefined) {
          value = dataInCompany
        } else if (t === 'bool') {
          value = false
        } else if (t === 'trans') {
          value = {}
        } else if (t === 'vue-select') {
          value = []
        } else if (t === 'hours') {
          value = {
            monday: {hours: []},
            tuesday: {hours: []},
            wednesday: {hours: []},
            thursday: {hours: []},
            friday: {hours: []},
            saturday: {hours: []},
            sunday: {hours: []},
          }
        }
        this.data[key] = value;
      }
    },

    getKeyInCompany(key) {
      if (!this.company) {
        return undefined;
      }
      let fragments = key.split('.')
      let curr = this.company;
      for (let f of fragments) {
        curr = curr[f]
      }
      return curr
    },

    _fixCompany(company) {
      company.bypassPosOptions = this._parseAsObject(company.bypassPosOptions)
      company.holdAndFireOptions = this._parseAsObject(company.holdAndFireOptions)
      company.emergencyCloseManuallyMessage = this._parseAsObject(company.emergencyCloseManuallyMessage)
    },

    _parseAsObject(t) {
      return t && typeof t === 'object' && t.length === undefined ? t : {}
    }
  }
}
</script>

<style lang="scss" scoped>
.subtitle {
  text-transform: uppercase;
  font-weight: bold;
  font-size: 14px;
  color: var(--primary-color);
  margin-bottom: 20px;
  line-height: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.week_hours {
  margin-bottom: 30px;
}
</style>